import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Avatar,
  Chip,
  Grid,
  FormControlLabel,
  Switch,
  ImageList,
  ImageListItem,
  Dialog,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ImageIcon from "@mui/icons-material/Image";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { toast } from "react-toast";
import interactWithUSComments from "../../../config/liveFirebase";
import "./Chat.scss";
import structureLocation from "../../../newcomponent/structureLocation";
const Chat = ({
  issue,
  onClose,
  projectId,
  onIssueUpdate,
  isNewIssue = false,
  locationOptions = [],
}) => {
  const [commentText, setCommentText] = useState("");
  const [changeStatus, setChangeStatus] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [resolutions, setResolutions] = useState({});
  const [loading, setLoading] = useState(true);
  const [sendingResolution, setSendingResolution] = useState(false);
  const [selectedImageForPreview, setSelectedImageForPreview] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [resolutionsListener, setResolutionsListener] = useState(null);
  const [currentIssue, setCurrentIssue] = useState(issue);
  const [uploadProgress, setUploadProgress] = useState({}); // Changed to object for tracking multiple uploads
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerImages, setImageViewerImages] = useState([]);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);
  const [location, setLocation] = useState(""); // New state for location
  const [locationError, setLocationError] = useState(""); // Error state for location
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar open state
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Snackbar severity

  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const commentsRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setCurrentIssue(issue);
  }, [issue]);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get the userName from localStorage
  const getUserName = () => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        const user = JSON.parse(userString);
        return user.name || "Admin User";
      }
      return "Admin User";
    } catch (error) {
      console.error("Error getting user name:", error);
      return "Admin User";
    }
  };

  // Set up real-time listener for resolutions
  useEffect(() => {
    if (issue?.path && issue?.id) {
      let isListening = true;

      const fetchResolutions = async () => {
        setLoading(true);
        try {
          const response = await interactWithUSComments(
            "GET",
            `${issue.path}/quality/${issue.id}/resolutions`,
            null
          );

          if (response.status === "Success" && response.data && isListening) {
            setResolutions(response.data);
          } else if (isListening) {
            setResolutions({});
          }
        } catch (error) {
          console.error("Error fetching resolutions:", error);
          if (isListening) setResolutions({});
        } finally {
          if (isListening) setLoading(false);
        }
      };

      fetchResolutions();

      // Set up real-time polling for updates
      const pollInterval = setInterval(() => {
        if (isListening) {
          interactWithUSComments(
            "GET",
            `${issue.path}/quality/${issue.id}/resolutions`,
            null
          )
            .then((response) => {
              if (
                isListening &&
                response.status === "Success" &&
                response.data
              ) {
                setResolutions((prevResolutions) => {
                  // Only update if there's a change to avoid unnecessary re-renders
                  const prevKeys = Object.keys(prevResolutions)
                    .sort()
                    .join(",");
                  const newKeys = Object.keys(response.data).sort().join(",");

                  if (
                    prevKeys !== newKeys ||
                    JSON.stringify(prevResolutions) !==
                      JSON.stringify(response.data)
                  ) {
                    return response.data;
                  }
                  return prevResolutions;
                });
              }
            })
            .catch((error) => {
              console.error("Error polling resolutions:", error);
            });
        }
      }, 3000); // Poll every 3 seconds

      setResolutionsListener(pollInterval);

      return () => {
        isListening = false;
        if (resolutionsListener) {
          clearInterval(resolutionsListener);
        }
      };
    }
  }, [issue?.path, issue?.id]);

  // Clean up polling when component unmounts
  useEffect(() => {
    return () => {
      if (resolutionsListener) {
        clearInterval(resolutionsListener);
      }
    };
  }, [resolutionsListener]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [resolutions]);

  // Convert a file to base64 with compression
  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      // Create a FileReader to read the file
      const reader = new FileReader();

      // Create an image for compression
      const img = new Image();

      // Read file as data URL
      reader.readAsDataURL(file);

      reader.onload = () => {
        // Set up image for compression
        img.src = reader.result;
        img.onload = () => {
          // Create canvas for compression
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          // Using smaller dimensions for better compression
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw image to canvas with new dimensions
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // Get compressed image as data URL with higher compression
          const quality = 0.5; // 50% quality for better compression
          const dataURL = canvas.toDataURL(file.type, quality);

          resolve(dataURL);
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Split data to avoid Firebase size limits
  const splitDataForFirebase = (data) => {
    const MAX_SIZE = 200 * 1024; // 200KB to be safe
    const jsonData = JSON.stringify(data);

    if (jsonData.length <= MAX_SIZE) {
      return [data]; // No need to split
    }

    // We need to split the data - in this case we'll split by images
    const result = [];
    const baseData = { ...data };
    delete baseData.images;

    // Base data always includes the description and metadata
    result.push(baseData);

    // Add images one by one
    if (data.images) {
      Object.entries(data.images).forEach(([imageId, imageData]) => {
        result.push({
          imageChunk: true,
          imageId,
          imageData,
          timestamp: data.timestamp,
          timeStr: data.timeStr,
          by: data.by,
          isResolution: data.isResolution,
        });
      });
    }

    return result;
  };

  // Add enhanced image upload functionality with progress tracking
  const handleImageUpload = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const imageTimestamp = Date.now();
        const imageId = `${imageTimestamp}`;
        const fileExtension = file.name.split(".").pop() || "jpg";
        const storagePath = `${currentIssue.path}/quality/image_upload_${imageTimestamp}.${fileExtension}`;

        // Create a unique ID for tracking this upload
        const uploadId = `upload_${imageTimestamp}`;

        // Add to uploads tracking with initial state
        setUploadProgress((prev) => ({
          ...prev,
          [uploadId]: {
            progress: 0,
            id: uploadId,
            file,
            tempUri: URL.createObjectURL(file),
          },
        }));

        // Compress and upload the image
        const imageUrl = await uploadImageToFirebaseStorage(
          file,
          storagePath,
          (progress) => {
            // Update progress for this specific upload
            setUploadProgress((prev) => ({
              ...prev,
              [uploadId]: {
                ...prev[uploadId],
                progress,
              },
            }));
          }
        );

        // Remove from progress tracking
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[uploadId];
          return newProgress;
        });

        // Return the completed upload info
        resolve({
          url: imageUrl,
          type: fileExtension,
          id: imageId,
        });
      } catch (error) {
        console.error("Error during image upload:", error);
        reject(error);
      }
    });
  };

  // Enhanced version of uploadImageToFirebaseStorage that reports progress
  const uploadImageToFirebaseStorage = async (
    file,
    storagePath,
    progressCallback = () => {}
  ) => {
    return new Promise((resolve, reject) => {
      // Skip compression for smaller images (under 1MB)
      if (file.size < 1024 * 1024) {
        progressCallback(20); // Skip compression steps
        // Directly upload the file
        interactWithUSComments("UPLOAD_IMAGE", storagePath, file)
          .then((response) => {
            progressCallback(100);
            if (response && response.status === "Success" && response.data) {
              resolve(response.data);
            } else {
              reject(new Error("Failed to get image URL"));
            }
          })
          .catch(reject);
        return;
      }

      // For larger files, use web workers for faster compression
      progressCallback(10); // Started processing

      // Create a compressed version of the image before uploading
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
        progressCallback(20); // Image loaded

        img.onload = async () => {
          // Compress image - use faster compression with lower quality
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 1000; // Reduced from 1200
          const MAX_HEIGHT = 1000; // Reduced from 1200
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }
          }

          progressCallback(30); // Image dimensions calculated

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          progressCallback(50); // Image drawn to canvas

          // Convert to blob with reduced quality
          canvas.toBlob(
            async (blob) => {
              try {
                progressCallback(60); // Blob created, ready to upload

                // Now use Firebase Storage API to upload the blob
                const response = await interactWithUSComments(
                  "UPLOAD_IMAGE",
                  storagePath,
                  blob
                );

                progressCallback(95); // Upload completed

                if (
                  response &&
                  response.status === "Success" &&
                  response.data
                ) {
                  progressCallback(100); // Process completed
                  resolve(response.data); // This should be the download URL
                } else {
                  reject(new Error("Failed to get image URL"));
                }
              } catch (err) {
                reject(err);
              }
            },
            file.type,
            0.5 // More aggressive compression (changed from 0.7)
          );
        };
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Preprocess resolutions to combine image chunks
  const preprocessResolutions = (resolutionsData) => {
    if (!resolutionsData) return {};

    const result = { ...resolutionsData };
    const toRemove = [];

    // First pass - identify image chunks and combine them with parent messages
    Object.entries(resolutionsData).forEach(([key, value]) => {
      if (value.parentTimestamp && value.imageChunk) {
        const parentKey = value.parentTimestamp.toString();

        // If parent exists, add this image to it
        if (result[parentKey]) {
          if (!result[parentKey].images) {
            result[parentKey].images = {};
          }

          result[parentKey].images[value.imageId] = value.imageData;
          toRemove.push(key);
        }
      }
    });

    // Remove processed chunks
    toRemove.forEach((key) => {
      delete result[key];
    });

    return result;
  };

  // Sort the resolutions by timestamp
  // Process resolutions to combine image chunks with their parent messages
  const processedResolutions = preprocessResolutions(resolutions);
  const sortedResolutions = Object.entries(processedResolutions)
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .map(([key, value]) => ({ key, ...value }));

  // Handle image selection - Allow selecting multiple images
  const handleImageSelection = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    // Prevent selecting too many images
    if (selectedImages.length + e.target.files.length > 10) {
      alert("You can upload a maximum of 10 images at once.");
      return;
    }

    // Convert FileList to array and process each image
    const newImages = Array.from(e.target.files)
      .map((file) => {
        // Validate file type
        if (!file.type.startsWith("image/")) {
          console.error("Not an image file:", file.name);
          return null;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          console.error("File too large:", file.name);
          return null;
        }

        // Create preview URL for UI
        return {
          file,
          preview: URL.createObjectURL(file),
          id: `temp_${Date.now()}_${Math.random()
            .toString(36)
            .substring(2, 9)}`,
        };
      })
      .filter(Boolean); // Remove any invalid files

    // Add to existing selected images
    setSelectedImages((prev) => [...prev, ...newImages]);

    // Clear the input to allow selecting the same file again
    e.target.value = null;
  };

  // Function to remove a selected image
  const removeSelectedImage = (index) => {
    setSelectedImages((prev) => {
      const updatedImages = [...prev];
      // Release object URL to prevent memory leaks
      if (updatedImages[index]?.preview) {
        URL.revokeObjectURL(updatedImages[index].preview);
      }
      updatedImages.splice(index, 1);
      return updatedImages;
    });
  };

  // Function to open the image viewer with all images from an object
  const handleImageGalleryOpen = (images, initialIndex = 0) => {
    if (!images || typeof images !== "object") return;

    // Convert object of images to array of URLs
    const imageUrls = Object.values(images)
      .filter((img) => img && img.url) // Filter out any invalid images
      .map((img) => img.url);

    if (imageUrls.length === 0) return;

    // Ensure initial index is in bounds
    const safeIndex = Math.min(Math.max(0, initialIndex), imageUrls.length - 1);

    // Open the image viewer modal
    setImageViewerImages(imageUrls);
    setImageViewerIndex(safeIndex);
    setImageViewerOpen(true);
  };

  // Handle sending a resolution/comment
  const handleSendComment = async () => {
    // New validation - if changing status, require BOTH text AND images
    if (changeStatus && (!commentText.trim() || selectedImages.length === 0)) {
      alert(
        "Please add both a comment AND upload at least one image when changing the issue status."
      );
      return;
    }

    // Original validation for other cases
    if (
      (!commentText.trim() && selectedImages.length === 0 && !changeStatus) ||
      isSubmitting
    )
      return;

    setSendingResolution(true);
    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      const userName = getUserName();
      const timestamp = new Date().getTime();
      const timeStr = new Date().toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });

      // Process and upload images to Firebase Storage first - PARALLEL IMPLEMENTATION
      const uploadedImages = {};

      if (selectedImages.length > 0) {
        setUploading(true);
        const totalImages = selectedImages.length;

        // Create array of upload promises to process in parallel
        const uploadPromises = selectedImages.map(async (image, index) => {
          try {
            // Set initial progress for this upload
            setUploadProgress((prev) => ({
              ...prev,
              [`upload_${index}`]: { progress: 0 },
            }));

            const file = image.file;
            const imageTimestamp = Date.now() + index; // Ensure unique timestamps
            const imageId = `${imageTimestamp}`;
            const fileExtension = file.name.split(".").pop() || "jpg";

            // Upload to Firebase Storage with progress tracking
            const imageUrl = await uploadImageToFirebaseStorage(
              file,
              `${currentIssue.path}/quality/image_upload_${imageTimestamp}.${fileExtension}`,
              (progress) => {
                // Update progress for this specific upload
                setUploadProgress((prev) => ({
                  ...prev,
                  [`upload_${index}`]: { progress },
                }));
              }
            );

            // Return the upload result for this image
            return {
              imageId,
              data: {
                url: imageUrl,
                type: fileExtension,
              },
            };
          } catch (error) {
            console.error("Error uploading image to Firebase Storage:", error);
            return null; // Return null for failed uploads
          }
        });

        // Wait for all uploads to complete simultaneously
        const results = await Promise.all(uploadPromises);

        // Process results and filter out failed uploads
        results.filter(Boolean).forEach((result) => {
          uploadedImages[result.imageId] = result.data;
        });

        setUploadProgress(40); // Image uploads complete
        setUploading(false);
      }

      // Update status if requested
      if (changeStatus) {
        // First update the status in the issue metadata
        const newStatus = currentIssue.status === "Open" ? "Closed" : "Open";
        const statusUpdate = {
          status: newStatus,
          lastStatusChangeBy: userName,
          lastStatusChangeAt: timeStr,
        };

        console.log("Updating issue status:", newStatus);
        setUploadProgress(50);

        // Update the issue status in the database, but keep the resolutions object intact
        // Get the current issue first with all its data to ensure we don't lose resolutions
        const currentIssueResponse = await interactWithUSComments(
          "GET",
          `${currentIssue.path}/quality/${currentIssue.id}`,
          null
        );

        // Only update if successful response
        if (
          currentIssueResponse.status === "Success" &&
          currentIssueResponse.data
        ) {
          // Update the issue with new status but preserve all other data
          await interactWithUSComments(
            "POST",
            `${currentIssue.path}/quality/${currentIssue.id}`,
            {
              ...currentIssueResponse.data,
              ...statusUpdate,
            }
          );
        } else {
          // Fallback if fetch fails
          await interactWithUSComments(
            "POST",
            `${currentIssue.path}/quality/${currentIssue.id}`,
            { ...currentIssue, ...statusUpdate }
          );
        }

        setUploadProgress(60);

        // Create a status update entry
        const statusEntry = {
          timestamp: timestamp,
          timeStr,
          by: userName,
          type: "status",
          status: newStatus,
        };

        // Save the status update entry
        await interactWithUSComments(
          "POST",
          `${currentIssue.path}/quality/${currentIssue.id}/resolutions/${timestamp}`,
          statusEntry
        );

        setUploadProgress(70);

        // If we also have a comment or images, create a separate entry
        if (commentText.trim() || Object.keys(uploadedImages).length > 0) {
          const commentTimestamp = timestamp + 1;

          // Create the comment entry
          const commentData = {
            timestamp: commentTimestamp,
            timeStr,
            by: userName,
          };

          // Add description if any
          if (commentText.trim()) {
            commentData.description = commentText.trim();
          }

          // Add images if any
          if (Object.keys(uploadedImages).length > 0) {
            commentData.images = uploadedImages;
          }

          // Save the comment as an additional resolution
          await interactWithUSComments(
            "POST",
            `${currentIssue.path}/quality/${currentIssue.id}/resolutions/${commentTimestamp}`,
            commentData
          );
        }

        setUploadProgress(80);

        // Update local state to reflect status change
        setCurrentIssue((prev) => ({
          ...prev,
          ...statusUpdate,
        }));

        // Notify parent about the update
        if (onIssueUpdate) {
          onIssueUpdate({
            ...currentIssue,
            ...statusUpdate,
          });
        }
      } else {
        // Regular comment without status change
        // Create a comment entry
        const commentData = {
          timestamp: timestamp,
          timeStr,
          by: userName,
        };

        // Add description if any
        if (commentText.trim()) {
          commentData.description = commentText.trim();
        }

        // Add images if any
        if (Object.keys(uploadedImages).length > 0) {
          commentData.images = uploadedImages;
        }

        // Save the comment
        console.log("Creating regular comment entry");
        await interactWithUSComments(
          "POST",
          `${currentIssue.path}/quality/${currentIssue.id}/resolutions/${timestamp}`,
          commentData
        );

        setUploadProgress(80);
      }

      setUploadProgress(100);

      // Clear the form
      setCommentText("");
      setSelectedImages([]);
      setChangeStatus(false);

      // Scroll to bottom to show latest comment
      if (bottomRef.current) {
        setTimeout(() => {
          bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    } catch (error) {
      console.error("Error sending resolution:", error);
      // Show error message
      alert("Error sending resolution. Please try again.");
    } finally {
      setSendingResolution(false);
      // Add a small delay before hiding the submit indicator
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    }
  };

  // Component for rendering upload progress
  const UploadProgressDisplay = ({ progressMap }) => {
    const uploads = Object.entries(progressMap);

    if (uploads.length === 0) return null;

    // Calculate the average progress across all uploads
    const totalProgress = uploads.reduce(
      (sum, [_, data]) => sum + data.progress,
      0
    );
    const averageProgress =
      uploads.length > 0 ? Math.round(totalProgress / uploads.length) : 0;

    return (
      <Box className="upload-progress-container">
        <Box className="upload-progress-bar-container">
          <Box
            className="upload-progress-bar"
            sx={{ width: `${averageProgress}%` }}
          />
        </Box>
        <Typography variant="caption" className="upload-progress-text">
          Uploading images... {averageProgress}%
        </Typography>
      </Box>
    );
  };

  // Handle scroll events to show/hide scroll buttons
  const handleContainerScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    setIsScrolled(scrollTop > 200);
  };

  // Scroll to bottom function for viewing newest comments
  const scrollToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // Add event listener for scroll
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleContainerScroll);
      return () => {
        container.removeEventListener("scroll", handleContainerScroll);
      };
    }
  }, []);

  // Create a file input style with proper dimensions for the preview images
  const fileInputStyle = {
    display: "none",
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [resolutions]);

  // Add event listener for scroll
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleContainerScroll);
      return () => {
        container.removeEventListener("scroll", handleContainerScroll);
      };
    }
  }, []);

  // State for handling new issues
  const [descriptionText, setDescriptionText] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [isCreatingNewIssue, setIsCreatingNewIssue] = useState(isNewIssue);
  const [fetchingLocations, setFetchingLocations] = useState(false);
  const [locations, setLocations] = useState([]);

  // Update isCreatingNewIssue when isNewIssue prop changes
  useEffect(() => {
    setIsCreatingNewIssue(isNewIssue);

    // If this is a new issue and we don't have location options, fetch them
    if (isNewIssue && locationOptions.length === 0 && projectId) {
      fetchLocations();
    } else if (isNewIssue) {
      // Use the location options passed as props
      setLocations(locationOptions);
    }
  }, [isNewIssue, locationOptions, projectId]);

  // Function to fetch locations directly using liveFirebase if needed
  const fetchLocations = async () => {
    if (!projectId) return;

    setFetchingLocations(true);

    try {
      // Fetch locations data from Firebase using interactWithUSComments
      const response = await interactWithUSComments(
        "GET",
        `${projectId}/Blocks`,
        null
      );

      if (response.status === "Success" && response.data) {
        // Process the location data using structureLocation helper
        const formattedLocations = structureLocation(response.data, projectId);
        console.log("Fetched locations:", formattedLocations);
        setLocations(formattedLocations);
      } else {
        console.log("No location data found or fetch failed");
        setLocations([]);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    } finally {
      setFetchingLocations(false);
    }
  };

  // Handle creating a new issue
  const handleCreateNewIssue = async () => {
    // Validate required fields
    if (!selectedLocation) {
      setLocationError("Please select a location");
      return;
    }

    if (!descriptionText.trim()) {
      alert("Please enter a description for the issue");
      return;
    }

    setIsSubmitting(true);

    try {
      const userName = getUserName();
      const timestamp = new Date().getTime();
      const dateStr = new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timeStr = new Date().toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });

      // Upload images if any
      const uploadedImages = {};

      if (selectedImages.length > 0) {
        setUploading(true);

        const uploadPromises = selectedImages.map(async (image, index) => {
          try {
            setUploadProgress((prev) => ({
              ...prev,
              [`upload_${index}`]: { progress: 0 },
            }));

            const file = image.file;
            const imageTimestamp = timestamp + index;
            const imageId = `${imageTimestamp}`;
            const fileExtension = file.name.split(".").pop() || "jpg";

            const imageUrl = await uploadImageToFirebaseStorage(
              file,
              `${projectId}/Quality/image_upload_${imageTimestamp}.${fileExtension}`,
              (progress) => {
                setUploadProgress((prev) => ({
                  ...prev,
                  [`upload_${index}`]: { progress },
                }));
              }
            );

            return {
              imageId,
              data: {
                url: imageUrl,
                type: fileExtension,
              },
            };
          } catch (error) {
            console.error("Error uploading image:", error);
            return null;
          }
        });

        const results = await Promise.all(uploadPromises);

        results.filter(Boolean).forEach((result) => {
          uploadedImages[result.imageId] = result.data;
        });

        setUploading(false);
      }

      // Find the selected location object from the locations array to get its path
      let selectedPath = "";
      const locationObj = (
        locations.length > 0 ? locations : locationOptions
      ).find((loc) => loc.displayText === selectedLocation);

      if (locationObj && locationObj.path) {
        selectedPath = locationObj.path;
      } else {
        // If location path not found, use fallback (this shouldn't happen if UI validation is working)
        const today = new Date();
        const formattedDate = `${today.getDate()}-${(today.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${today.getFullYear()}`;
        selectedPath = `${projectId}/miscellaneous/dateStamp:${formattedDate}`;
        console.warn("Location path not found, using miscellaneous path");
      }

      // Create the new issue object
      const newIssueId = timestamp.toString();
      const newIssue = {
        id: newIssueId,
        location: selectedLocation,
        description: descriptionText.trim(),
        status: "Open",
        by: userName,
        timestamp: timeStr,
        date: dateStr,
        path: `${projectId}/Quality`,
        team: userName,
      };

      // Add images if any
      if (Object.keys(uploadedImages).length > 0) {
        newIssue.images = uploadedImages;
      }

      console.log("Creating new issue:", newIssue);
      console.log("Selected path:", selectedPath);

      // Save the issue at the specific location path exactly as in AddOrEditQuality
      // This is the only place we save the issue - in the location path from structure location
      await interactWithUSComments(
        "POST",
        `${selectedPath}/quality/${newIssueId}`,
        newIssue
      );

      // Format the date as a timestamp for the date key to maintain compatibility with date structure
      // but store only a reference to the actual issue location
      const dateObj = new Date();
      const year = dateObj.getFullYear();
      const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
      const day = dateObj.getDate().toString().padStart(2, "0");
      const dateKey = `${year}_${month}_${day}`;

      // Add to the date-based structure as a reference
      await interactWithUSComments(
        "GET",
        `${projectId}/Quality/byDate/${dateKey}`,
        null
      ).then(async (response) => {
        // If the date already exists, add to its items
        if (response.status === "Success" && response.data) {
          const dateData = response.data;
          if (!dateData.items) dateData.items = [];

          // Add a reference to the issue to the items array
          const issueReference = {
            id: newIssueId,
            location: selectedLocation,
            status: "Open",
            path: selectedPath,
            isReference: true,
          };

          dateData.items.push(issueReference);

          // Update the date entry
          await interactWithUSComments(
            "POST",
            `${projectId}/Quality/byDate/${dateKey}`,
            dateData
          );
        } else {
          // Create a new date entry with this issue reference
          const dateEntry = {
            date: dateStr,
            id: dateKey,
            items: [
              {
                id: newIssueId,
                location: selectedLocation,
                status: "Open",
                path: selectedPath,
                isReference: true,
              },
            ],
          };

          await interactWithUSComments(
            "POST",
            `${projectId}/Quality/byDate/${dateKey}`,
            dateEntry
          );
        }
      });

      // Update the current issue with the newly created one
      setCurrentIssue(newIssue);

      // Notify parent about the new issue
      if (onIssueUpdate) {
        onIssueUpdate(newIssue);
      }

      // Clear the form after successful creation
      setIsCreatingNewIssue(false);
      setDescriptionText("");
      setSelectedLocation("");
      setSelectedImages([]);

      // Show success message
      setSnackbarMessage("Issue created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Close the form - instead of using setIsNewIssue, use the onClose prop
      // to let the parent component handle closing the form
      onClose();
    } catch (error) {
      console.error("Error creating issue:", error);
      setSnackbarMessage("Error creating issue. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="quality-chat-container" ref={containerRef}>
      {/* Header - Fixed at top */}
      <Paper elevation={2} className="chat-header">
        <IconButton
          onClick={onClose}
          className="back-button"
          sx={{ color: "#222" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
          {isNewIssue ? "New Quality Issue" : "Quality Issue Details"}
        </Typography>

        {/* Show status badge only for existing issues */}
        {!isNewIssue && currentIssue?.status && (
          <Box sx={{ marginLeft: "auto" }}>
            <Chip
              icon={
                currentIssue?.status === "Open" ? (
                  <ErrorIcon />
                ) : (
                  <CheckCircleIcon />
                )
              }
              label={currentIssue?.status || "Unknown"}
              color={currentIssue?.status === "Open" ? "error" : "success"}
              variant="filled"
              className="status-chip"
            />
          </Box>
        )}
      </Paper>

      {isNewIssue ? (
        // New Issue Form
        <Box className="unified-scroll-container new-issue-container">
          <Paper elevation={0} className="issue-details-paper">
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Create New Quality Issue
            </Typography>

            {/* Location selection dropdown */}
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ mb: 3 }}
              error={!!locationError}
              required
            >
              <InputLabel id="location-select-label">Location</InputLabel>
              <Select
                labelId="location-select-label"
                id="location-select"
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setLocationError("");
                }}
                label="Location*"
              >
                {locations.length > 0 || locationOptions.length > 0 ? (
                  // Process locations to include nested points
                  (() => {
                    // Get the source data from locations or locationOptions
                    const locData =
                      locations.length > 0 ? locations : locationOptions;
                    // Create an array to hold all menu items including points
                    const menuItems = [];

                    // Process each location item (blocks and floors) and their nested points
                    locData.forEach((loc) => {
                      // Add the main location (block or floor)
                      menuItems.push(
                        <MenuItem
                          key={`${loc.type}-${loc.id}`}
                          value={loc.displayText}
                          sx={{
                            pl: 1,
                            fontWeight: loc.type === "block" ? 600 : 400,
                          }}
                        >
                          {loc.displayText}
                        </MenuItem>
                      );

                      // Add points if this location has them
                      if (
                        loc.pointDetails &&
                        Array.isArray(loc.pointDetails) &&
                        loc.pointDetails.length > 0
                      ) {
                        loc.pointDetails.forEach((point) => {
                          menuItems.push(
                            <MenuItem
                              key={`point-${point.id}-${loc.id}`}
                              value={point.displayText}
                              sx={{
                                pl: 1, // Left aligned
                                fontWeight: 400,
                              }}
                            >
                              {point.displayText}{" "}
                              {/* Display full hierarchical path */}
                            </MenuItem>
                          );
                        });
                      }
                    });

                    return menuItems;
                  })()
                ) : (
                  <MenuItem value="">
                    <em>No locations available</em>
                  </MenuItem>
                )}
              </Select>
              {locationError && (
                <FormHelperText>{locationError}</FormHelperText>
              )}
            </FormControl>

            {/* Issue description */}
            <TextField
              label="Issue Description"
              placeholder="Describe the quality issue in detail..."
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={descriptionText}
              onChange={(e) => setDescriptionText(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            {/* Images section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Add Images{" "}
                <span style={{ color: "#666", fontSize: "0.9rem" }}>
                  (Optional)
                </span>
              </Typography>

              <Button
                variant="outlined"
                startIcon={<CameraAltIcon />}
                onClick={handleImageClick}
                sx={{ mb: 2 }}
              >
                Select Images
              </Button>

              <input
                type="file"
                accept="image/*"
                multiple
                ref={fileInputRef}
                style={fileInputStyle}
                onChange={handleImageSelection}
              />

              {/* Selected images preview */}
              {selectedImages.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  {selectedImages.map((image, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        width: 120,
                        height: 120,
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={image.preview}
                        alt={`Image ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeSelectedImage(index)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          width: 20,
                          height: 20,
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Submit button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onClose}
                sx={{ mr: 2 }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateNewIssue}
                disabled={
                  !selectedLocation || !descriptionText.trim() || isSubmitting
                }
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? "Creating..." : "Create Issue"}
              </Button>
            </Box>

            {/* Upload progress */}
            {isSubmitting && Object.keys(uploadProgress).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Uploading images...
                </Typography>
                <UploadProgressDisplay progressMap={uploadProgress} />
              </Box>
            )}
          </Paper>
        </Box>
      ) : (
        // Existing Issue View
        <Box className="unified-scroll-container">
          {/* Issue details section */}
          <Paper elevation={0} className="issue-details-paper">
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#1e293b", mb: 2 }}
            >
              {currentIssue?.location || "Unknown Location"}
            </Typography>

            {currentIssue?.lastStatusChangeBy && (
              <Typography
                variant="caption"
                className="status-changed-by"
                sx={{
                  display: "block",
                  mb: 2,
                  fontStyle: "italic",
                  color: "#64748b",
                }}
              >
                Last changed by {currentIssue.lastStatusChangeBy} at{" "}
                {currentIssue.lastStatusChangeAt || "unknown time"}
              </Typography>
            )}

            {/* Location */}
            <Box className="detail-section">
              <LocationOnIcon className="detail-icon" />
              <Typography
                variant="subtitle1"
                className="detail-text"
                sx={{ fontWeight: 500 }}
              >
                {currentIssue?.location || "Unknown Location"}
              </Typography>
            </Box>

            {/* Reporter */}
            <Box className="detail-section">
              <PersonIcon className="detail-icon" />
              <Typography variant="body1" className="detail-text">
                Reported by <b>{currentIssue?.by || "Unknown"}</b> on{" "}
                {currentIssue?.date || "Unknown date"}
              </Typography>
            </Box>

            {/* Description */}
            <Box className="description-section">
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Description
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#475569",
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.6,
                }}
              >
                {currentIssue?.description || "No description provided."}
              </Typography>
            </Box>

            {/* Images section */}
            {currentIssue?.images &&
              Object.keys(currentIssue.images).length > 0 && (
                <Box className="images-section">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: "#1e293b",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <ImageIcon
                      sx={{
                        marginRight: 1,
                        fontSize: 20,
                        verticalAlign: "middle",
                        color: "#4b6bfb",
                      }}
                    />
                    Images ({Object.keys(currentIssue.images).length})
                  </Typography>
                  <ImageList
                    cols={3}
                    gap={12}
                    className="image-list"
                    sx={{
                      mt: 1,
                      mb: 0,
                    }}
                  >
                    {Object.values(currentIssue.images).map((image, index) => (
                      <ImageListItem key={index} className="image-item">
                        <img
                          src={image.url}
                          alt={`Issue image ${index + 1}`}
                          loading="lazy"
                          onClick={() => {
                            setSelectedImageForPreview(image.url);
                            setShowImagePreview(true);
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
          </Paper>

          {/* Timeline connector between issue details and comments */}
          <Box className="timeline-connector">
            <Box className="timeline-line"></Box>
          </Box>

          {/* Comments/Resolutions Section - now part of the same scrollable container */}
          <Box className="comments-section">
            <Typography variant="h6" className="section-title">
              Comments & Activity
            </Typography>

            {loading ? (
              <Box className="loading-container">
                <CircularProgress size={32} sx={{ color: "#4b6bfb" }} />
                <Typography variant="body2">Loading comments...</Typography>
              </Box>
            ) : sortedResolutions.length > 0 ? (
              <Box className="comments-list" ref={commentsRef}>
                {sortedResolutions.map((resolution) => (
                  <Box
                    key={resolution.key}
                    className={`comment-item ${
                      resolution.type === "status"
                        ? `status-update ${resolution.status}`
                        : resolution.isResolution
                        ? "resolution-comment"
                        : ""
                    } ${
                      resolution.by === getUserName()
                        ? "my-comment"
                        : "other-comment"
                    }`}
                  >
                    {resolution.type === "status" ? (
                      // Status update - centered with color coding based on status
                      <Box className="status-update-content">
                        <Chip
                          icon={
                            resolution.status === "Open" ? (
                              <ErrorIcon />
                            ) : (
                              <CheckCircleIcon />
                            )
                          }
                          label={`Issue ${
                            resolution.status === "Open"
                              ? "Reopened"
                              : "Resolved"
                          }`}
                          color={
                            resolution.status === "Open" ? "error" : "success"
                          }
                          variant="outlined"
                          size="small"
                        />
                        <Typography
                          variant="caption"
                          className="resolution-meta"
                        >
                          {resolution.by} •{" "}
                          {formatTimestamp(resolution.timestamp)}
                        </Typography>
                      </Box>
                    ) : (
                      // Regular comment or resolution
                      <Box
                        className={`comment-content ${
                          resolution.isResolution ? "resolution-content" : ""
                        }`}
                      >
                        <Box className="comment-header">
                          <Avatar
                            className={`comment-avatar ${
                              resolution.isResolution ? "resolution-avatar" : ""
                            }`}
                          >
                            {resolution.by
                              ? resolution.by.charAt(0).toUpperCase()
                              : "U"}
                          </Avatar>
                          <Box className="comment-author">
                            <Typography variant="subtitle2">
                              {resolution.by}
                              {resolution.isResolution && (
                                <span className="resolution-label">
                                  {" "}
                                  (Resolution)
                                </span>
                              )}
                            </Typography>
                            <Typography variant="caption">
                              {formatTimestamp(resolution.timestamp)}
                            </Typography>
                          </Box>
                        </Box>

                        {resolution.description && (
                          <Typography
                            variant="body1"
                            className={`comment-text ${
                              resolution.isResolution ? "resolution-text" : ""
                            }`}
                          >
                            {resolution.description}
                          </Typography>
                        )}

                        {/* Resolution Images */}
                        {resolution.images &&
                          Object.values(resolution.images).length > 0 && (
                            <ImageList
                              cols={4}
                              gap={8}
                              className={`comment-image-list ${
                                resolution.isResolution
                                  ? "resolution-image-list"
                                  : ""
                              }`}
                              sx={{ maxHeight: 300 }}
                            >
                              {Object.values(resolution.images).map(
                                (image, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      width: "120px",
                                      height: "120px",
                                      display: "inline-block",
                                      margin: "5px",
                                      border: "2px solid #e0e0e0",
                                      borderRadius: "4px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <img
                                      src={image.url}
                                      alt={`Comment image ${index + 1}`}
                                      loading="lazy"
                                      style={{
                                        width: "120px",
                                        height: "120px",
                                        objectFit: "cover",
                                      }}
                                      onClick={() => {
                                        setSelectedImageForPreview(image.url);
                                        setShowImagePreview(true);
                                      }}
                                    />
                                  </div>
                                )
                              )}
                            </ImageList>
                          )}
                      </Box>
                    )}
                  </Box>
                ))}
                <div ref={bottomRef} />
              </Box>
            ) : (
              <Box className="no-comments">
                <Typography variant="body2">No comments yet</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Comment Input Area - Only show for existing issues */}
      {!isNewIssue && (
        <Paper elevation={3} className="comment-input-area">
          {/* Upload progress indicator */}
          {isSubmitting && (
            <Box className="upload-progress-container">
              <Box className="upload-progress-bar-container">
                <Box
                  className="upload-progress-bar"
                  sx={{ width: `${uploadProgress}%` }}
                />
              </Box>
              <Typography variant="caption" className="upload-progress-text">
                {uploadProgress < 100
                  ? `Uploading... ${uploadProgress}%`
                  : "Processing..."}
              </Typography>
            </Box>
          )}

          {/* Selected images preview with explicit styling for the Mark as Resolved area */}
          {selectedImages.length > 0 && !isSubmitting && (
            <div
              style={{
                marginTop: "12px",
                marginBottom: "12px",
                padding: "10px",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  justifyContent: "flex-start",
                }}
              >
                {selectedImages.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      width: "120px",
                      height: "120px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "4px",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <img
                      src={image.preview}
                      alt={`Selected image ${index + 1}`}
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeSelectedImage(index)}
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        backgroundColor: "rgba(255,255,255,0.8)",
                        padding: "2px",
                        width: "24px",
                        height: "24px",
                        minWidth: "24px",
                      }}
                    >
                      <CloseRoundedIcon fontSize="small" color="error" />
                    </IconButton>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status change toggle and preview - iOS style */}
          {currentIssue?.status && !isSubmitting && (
            <Box
              className={`status-toggle ${
                currentIssue.status === "Open"
                  ? "resolve-toggle"
                  : "reopen-toggle"
              }`}
              sx={{
                marginBottom: "12px",
                padding: "16px",
                backgroundColor: changeStatus
                  ? currentIssue?.status === "Open"
                    ? "rgba(46, 125, 50, 0.1)"
                    : "rgba(211, 47, 47, 0.1)"
                  : "rgba(0, 0, 0, 0.04)",
                borderRadius: "12px",
                transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                boxShadow: changeStatus ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                border: changeStatus
                  ? `1px solid ${
                      currentIssue?.status === "Open"
                        ? "rgba(46, 125, 50, 0.2)"
                        : "rgba(211, 47, 47, 0.2)"
                    }`
                  : "1px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={changeStatus}
                    onChange={() => setChangeStatus(!changeStatus)}
                    sx={{
                      "& .MuiSwitch-switchBase": {
                        margin: 1,
                        padding: 0,
                        transform: "translateX(6px)",
                        "&.Mui-checked": {
                          color: "#fff",
                          transform: "translateX(22px)",
                          "& .MuiSwitch-thumb:before": {
                            backgroundImage:
                              currentIssue?.status === "Open"
                                ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                                    "#fff"
                                  )}" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>')`
                                : `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                                    "#fff"
                                  )}" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>')`,
                          },
                          "& + .MuiSwitch-track": {
                            opacity: 1,
                            backgroundColor:
                              currentIssue?.status === "Open"
                                ? "#2e7d32"
                                : "#d32f2f",
                          },
                        },
                      },
                      "& .MuiSwitch-thumb": {
                        backgroundColor: changeStatus
                          ? currentIssue?.status === "Open"
                            ? "#2e7d32"
                            : "#d32f2f"
                          : "white",
                        width: 22,
                        height: 22,
                        "&:before": {
                          content: "''",
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          left: 0,
                          top: 0,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                        },
                      },
                      "& .MuiSwitch-track": {
                        opacity: 1,
                        backgroundColor: changeStatus
                          ? currentIssue?.status === "Open"
                            ? "#2e7d32"
                            : "#d32f2f"
                          : "#E9E9EA",
                        borderRadius: 20 / 2,
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", flexDirection: "column", ml: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: changeStatus
                          ? currentIssue?.status === "Open"
                            ? "#2e7d32"
                            : "#d32f2f"
                          : "text.secondary",
                        fontWeight: changeStatus ? 600 : 400,
                        transition: "color 0.3s",
                      }}
                    >
                      Mark as{" "}
                      <strong>
                        {currentIssue?.status === "Open" ? "Resolved" : "Open"}
                      </strong>
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}

          {/* Input field and send button */}
          <Box className="comment-input-row">
            <IconButton
              className="attach-button"
              onClick={handleImageClick}
              disabled={isSubmitting}
              style={{
                width: "40px",
                height: "40px",
              }}
            >
              <CameraAltIcon />
            </IconButton>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              style={fileInputStyle}
              onChange={handleImageSelection}
            />
            <TextField
              className="comment-input"
              placeholder={
                changeStatus
                  ? `Add a comment about ${
                      currentIssue?.status === "Open"
                        ? "resolving"
                        : "reopening"
                    } this issue...`
                  : "Add a comment..."
              }
              variant="outlined"
              fullWidth
              multiline
              maxRows={4}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={isSubmitting}
            />
            <Button
              variant="contained"
              color="primary"
              className="send-button"
              onClick={handleSendComment}
              disabled={
                (!commentText.trim() &&
                  selectedImages.length === 0 &&
                  !changeStatus) ||
                isSubmitting
              }
              endIcon={
                sendingResolution ? (
                  <CircularProgress size={20} />
                ) : (
                  <SendIcon />
                )
              }
            >
              Send
            </Button>
          </Box>
        </Paper>
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setShowImagePreview(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImageForPreview && (
            <img
              src={selectedImageForPreview}
              alt="Full size preview"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* ImageViewerModal component for enhanced image viewing experience  */}
      <ImageViewerModal
        isOpen={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        images={imageViewerImages}
        initialIndex={imageViewerIndex}
      />

      {/* Floating action buttons for scrolling */}
      {isScrolled && (
        <Tooltip title="Scroll to top">
          <IconButton
            onClick={scrollToTop}
            sx={{
              position: "fixed",
              right: 24,
              top: 90,
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "white",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
              zIndex: 10,
            }}
          >
            <KeyboardArrowUpIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ImageViewerModal component for enhanced image viewing experience
const ImageViewerModal = ({ isOpen, onClose, images, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset current index when modal opens or new images are provided
  useEffect(() => {
    if (isOpen && images && images.length > 0) {
      setCurrentIndex(Math.min(initialIndex, images.length - 1));
    }
  }, [isOpen, images, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e) {
      if (!isOpen) return;

      if (e.key === "ArrowLeft") {
        if (currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        }
      }
      if (e.key === "ArrowRight") {
        if (images && images.length > 0 && currentIndex < images.length - 1) {
          setCurrentIndex(currentIndex + 1);
        }
      }
      if (e.key === "Escape") onClose();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIndex, images, isOpen, onClose]);

  // Safety check for empty images array
  if (!images || images.length === 0) return null;

  // Navigation handlers
  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Ensure the active image exists
  const currentImage = images[currentIndex];
  if (!currentImage) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: "black",
          boxShadow: "none",
          margin: 0,
          maxHeight: "100vh",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "white",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 10,
            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Image counter */}
        <Box
          sx={{
            position: "absolute",
            left: 16,
            top: 16,
            backgroundColor: "rgba(0,0,0,0.5)",
            padding: "4px 10px",
            borderRadius: 4,
            zIndex: 10,
          }}
        >
          <Typography sx={{ color: "white" }}>
            {currentIndex + 1} / {images.length}
          </Typography>
        </Box>

        {/* Previous button - only show if not at first image */}
        {currentIndex > 0 && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              backgroundColor: "rgba(0,0,0,0.3)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" },
              zIndex: 10,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        {/* Image container */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 2,
          }}
        >
          <img
            src={currentImage.url || currentImage}
            alt={`Image ${currentIndex + 1}`}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Next button - only show if not at last image */}
        {currentIndex < images.length - 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              backgroundColor: "rgba(0,0,0,0.3)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" },
              zIndex: 10,
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        )}
      </Box>
    </Dialog>
  );
};

export default Chat;
